import React from "react";
import { Link } from "react-router-dom";
import TableLoader from "../../Loader/TableLoader";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import usePermissions from "../hooks/usePermissions";

/* ─── Scoped Styles ───────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  .msf-scope { font-family: 'Plus Jakarta Sans', sans-serif; }

  /* ── Page header ── */
  .msf-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 6px 0 22px;
    border-bottom: 1px solid #f0f2f7;
    margin-bottom: 26px;
  }
  .msf-page-title {
    font-size: 20px; font-weight: 800; color: #1a1f36;
    letter-spacing: -0.3px; margin: 0;
  }
  .msf-breadcrumb { display: flex; align-items: center; gap: 6px; list-style: none; margin: 0; padding: 0; }
  .msf-breadcrumb li { font-size: 12.5px; font-weight: 500; color: #8b92a9; }
  .msf-breadcrumb li a { color: #8b92a9; text-decoration: none; }
  .msf-breadcrumb li.active { color: #3b5bdb; font-weight: 600; }
  .msf-breadcrumb li + li::before { content: '/'; margin-right: 6px; color: #c8cdd9; }

  /* ── Add Button ── */
  .msf-add-btn {
    display: inline-flex; align-items: center; gap: 7px;
    background: linear-gradient(135deg, #3b5bdb 0%, #4c6ef5 100%);
    color: #fff !important; font-size: 13.5px; font-weight: 700;
    padding: 10px 20px; border-radius: 10px;
    text-decoration: none !important; border: none;
    box-shadow: 0 4px 14px rgba(59,91,219,0.3);
    transition: all 0.2s ease; letter-spacing: 0.1px;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .msf-add-btn:hover {
    transform: translateY(-2px); color: #fff !important;
    box-shadow: 0 8px 20px rgba(59,91,219,0.42);
    background: linear-gradient(135deg, #3451c7 0%, #4263eb 100%);
  }

  /* ── Card ── */
  .msf-card {
    border: 1px solid #eaecf4 !important;
    border-radius: 16px; overflow: hidden;
    box-shadow: 0 2px 16px rgba(28,36,75,0.07);
  }
  .msf-card-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 24px 16px;
    border-bottom: 1px solid #f0f2f7;
    background: #fafbff;
  }
  .msf-card-title {
    font-size: 15px; font-weight: 800; color: #1a1f36;
    display: flex; align-items: center; gap: 10px; letter-spacing: -0.2px;
  }
  .msf-count-chip {
    background: #eef1fb; color: #3b5bdb;
    font-size: 12px; font-weight: 700;
    padding: 3px 10px; border-radius: 20px;
  }

  /* ── Table ── */
  .msf-table { width: 100%; border-collapse: collapse; font-family: 'Plus Jakarta Sans', sans-serif; }
  .msf-table thead tr { background: #f8f9fe; }
  .msf-table thead th {
    padding: 12px 18px;
    font-size: 11px; font-weight: 700; color: #8b92a9;
    text-transform: uppercase; letter-spacing: 0.8px;
    border: none; white-space: nowrap;
  }
  .msf-table tbody tr {
    border-bottom: 1px solid #f0f2f7;
    transition: background 0.15s ease;
  }
  .msf-table tbody tr:last-child { border-bottom: none; }
  .msf-table tbody tr:hover { background: #f8f9fe; }
  .msf-table tbody td {
    padding: 14px 18px;
    font-size: 13.5px; color: #2d3450; font-weight: 500;
    border: none; vertical-align: middle;
  }

  /* ── Feature Name cell ── */
  .msf-name-cell { display: flex; align-items: flex-start; gap: 10px; }
  .msf-name-avatar {
    width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
    background: linear-gradient(135deg, #eef1fb, #dde3f8);
    border: 1.5px solid #d0d8f5;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px;
  }
  .msf-name-text { font-weight: 700; color: #1a1f36; font-size: 13.5px; line-height: 1.4; }
  .msf-name-desc { font-size: 11.5px; color: #8b92a9; font-weight: 500; margin-top: 2px; line-height: 1.3; }

  /* ── Feature Type badge ── */
  .msf-type-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 7px;
    font-size: 11.5px; font-weight: 700;
  }
  .msf-type-numeric { background: #eef1fb; color: #3b5bdb; border: 1px solid #d0d8f5; }
  .msf-type-boolean { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  .msf-type-other   { background: #f5f5f5; color: #6b7280; border: 1px solid #e0e0e0; }

  /* ── Feature Key chip ── */
  .msf-key-chip {
    display: inline-block;
    padding: 3px 9px; border-radius: 6px;
    background: #f1f3f9; border: 1px solid #e2e6f0;
    font-size: 12px; font-weight: 600; color: #4b5680;
    font-family: 'Courier New', monospace; letter-spacing: 0.3px;
  }

  /* ── Default Value ── */
  .msf-value-cell { font-weight: 700; color: #1a1f36; font-size: 13.5px; }
  .msf-value-bool-true  { color: #16a34a; }
  .msf-value-bool-false { color: #dc2626; }

  /* ── Order badge ── */
  .msf-order-badge {
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border-radius: 8px;
    background: #f1f3f9; border: 1px solid #e2e6f0;
    font-size: 12px; font-weight: 700; color: #4b5680;
  }

  /* ── Status badges ── */
  .msf-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 11px; border-radius: 8px;
    font-size: 12px; font-weight: 700;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .msf-badge-active   { background: #edfaf3; color: #0a8a4a; border: 1px solid #b8f0d4; cursor: pointer; transition: all .15s; }
  .msf-badge-active:hover { background: #d4f5e5; box-shadow: 0 2px 8px rgba(10,138,74,.15); }
  .msf-badge-inactive { background: #fff1f0; color: #d92b2b; border: 1px solid #ffc9c6; cursor: pointer; transition: all .15s; }
  .msf-badge-inactive:hover { background: #ffe4e3; box-shadow: 0 2px 8px rgba(217,43,43,.15); }
  .msf-badge-pending  { background: #fffbec; color: #b07800; border: 1px solid #fde8a0; }
  .msf-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .msf-dot-active   { background: #0a8a4a; animation: msf-pulse 2s infinite; box-shadow: 0 0 0 2px rgba(10,138,74,.2); }
  .msf-dot-inactive { background: #d92b2b; }
  @keyframes msf-pulse { 0%,100%{opacity:1} 50%{opacity:.4} }

  /* ── Action button ── */
  .msf-btn-edit {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 9px;
    background: #eef1fb; border: 1.5px solid #d0d8f5; color: #3b5bdb;
    font-size: 12px; text-decoration: none; transition: all .18s ease;
  }
  .msf-btn-edit:hover {
    background: #3b5bdb; border-color: #3b5bdb; color: #fff !important;
    box-shadow: 0 4px 12px rgba(59,91,219,.35); transform: translateY(-1px);
    text-decoration: none;
  }

  /* ── Row animation ── */
  @keyframes msf-row-in { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
  .msf-row-in { animation: msf-row-in 0.26s ease both; }

  /* ── Empty state ── */
  .msf-empty { text-align: center; padding: 60px 24px; }
  .msf-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: .3; }
  .msf-empty p { color: #8b92a9; font-size: 14px; font-weight: 500; margin: 0; }
`;

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const typeIcon  = (t) => t === "numeric" ? "🔢" : t === "boolean" ? "✅" : "📝";
const typeClass = (t) => t === "numeric" ? "msf-type-numeric" : t === "boolean" ? "msf-type-boolean" : "msf-type-other";

const ManageSubFeatures = () => {
  const { canCreate, canEdit } = usePermissions("/cms/manage-subscription-features");

  const [featureData, setFeatureData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    fetchFeatureData();
  }, []);

  const fetchFeatureData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/get-features`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` },
      });
      setFeatureData(response.data.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching feature data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = () => {};

  const handleDeleteFeature = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete the feature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.showLoading();
        try {
          const response = await axios.post(
            `${API_BASE_URL}/delete-features/${id}`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` },
            }
          );
          if (response.data.status == "success") {
            Swal.fire("Changed!", "The status has been updated.", "success");
            fetchFeatureData();
          }
        } catch (error) {
          console.log(error);
          Swal.fire("Error!", "Failed to change status.", "error");
        } finally {
          Swal.hideLoading();
        }
      }
    });
  };

  return (
    <>
      <style>{styles}</style>

      <div className="main-content msf-scope">
        <div className="page-content">
          <div className="container-fluid">

            {/* ── Page Header ── */}
            <div className="msf-page-header">
              <div>
                <h4 className="msf-page-title">Manage Subscription Features</h4>
                <ul className="msf-breadcrumb" style={{ marginTop: 6 }}>
                  <li><a href="javascript: void(0);">CMS</a></li>
                  <li className="active">Manage Subscription Features</li>
                </ul>
              </div>
              {canCreate && (
                <Link
                  to="/cms/manage-subscription-features/add-subscription-features"
                  className="msf-add-btn"
                >
                  + Add Feature
                </Link>
              )}
            </div>

            {/* ── Table Card ── */}
            <div className="row">
              <div className="col-lg-12">
                <div className="msf-card">

                  {/* Card Header */}
                  <div className="msf-card-header">
                    <div className="msf-card-title">
                      Feature List
                      <span className="msf-count-chip">{featureData?.length ?? 0}</span>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive">
                    <table className="msf-table">
                      <thead>
                        <tr>
                          <th>Feature Name</th>
                          <th>Feature Type</th>
                          <th>Feature Key</th>
                          <th>Feature Value</th>
                          <th>Feature Order</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <TableLoader count={6} />
                        ) : featureData?.length === 0 ? (
                          <tr>
                            <td colSpan={7}>
                              <div className="msf-empty">
                                <div className="msf-empty-icon">🧩</div>
                                <p>No features found.</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          featureData
                            ?.sort((a, b) => a.feature_order - b.feature_order)
                            ?.map((item, idx) => (
                              <tr
                                key={item.id}
                                className="msf-row-in"
                                style={{ animationDelay: `${idx * 30}ms` }}
                              >
                                {/* Feature Name */}
                                <td>
                                  <div className="msf-name-cell">
                                    <div className="msf-name-avatar">
                                      {typeIcon(item.feature_type)}
                                    </div>
                                    <div>
                                      <div className="msf-name-text">{item.name}</div>
                                      {item.description && (
                                        <div className="msf-name-desc">{item.description}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* Feature Type */}
                                <td>
                                  <span className={`msf-type-badge ${typeClass(item.feature_type)}`}>
                                    {item.feature_type}
                                  </span>
                                </td>

                                {/* Feature Key */}
                                <td>
                                  <span className="msf-key-chip">{item.feature_key}</span>
                                </td>

                                {/* Feature Value */}
                                <td>
                                  {item.feature_type === "boolean" ? (
                                    <span className={`msf-value-cell ${item.default_value === "true" ? "msf-value-bool-true" : "msf-value-bool-false"}`}>
                                      {item.default_value === "true" ? "✓ true" : "✗ false"}
                                    </span>
                                  ) : (
                                    <span className="msf-value-cell">{item.default_value}</span>
                                  )}
                                </td>

                                {/* Feature Order */}
                                <td>
                                  <span className="msf-order-badge">{item.feature_order}</span>
                                </td>

                                {/* Status */}
                                <td>
                                  {item.is_active == 1 ? (
                                    <span
                                      className="msf-badge msf-badge-active"
                                      onClick={() => handleStatusChange(item.id, item.is_active)}
                                      title="Click to deactivate"
                                    >
                                      <span className="msf-dot msf-dot-active" /> Active
                                    </span>
                                  ) : item.is_active == 0 ? (
                                    <span
                                      className="msf-badge msf-badge-inactive"
                                      onClick={() => handleStatusChange(item.id, item.is_active)}
                                      title="Click to activate"
                                    >
                                      <span className="msf-dot msf-dot-inactive" /> Inactive
                                    </span>
                                  ) : (
                                    <span className="msf-badge msf-badge-pending">Pending</span>
                                  )}
                                </td>

                                {/* Action */}
                                <td>
                                  {canEdit && (
                                    <Link
                                      to={`/cms/manage-subscription-features/edit-subscription-features/${item.id}`}
                                      className="msf-btn-edit"
                                      title="Edit"
                                    >
                                      <i className="fa fa-pen" />
                                    </Link>
                                  )}
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ManageSubFeatures;
